/**
 * Speculation Rules JS enhancements (deferred until idle after load).
 *
 * 1. Prefetch fallback for non-Chromium browsers (hover + touchstart)
 * 2. PDP → Collection prerender: proactively prerenders the collection the user
 *    came from so back-navigation is instant (desktop only)
 *
 * Collection menu tabs are NOT speculated (they carry data-no-prefetch): a tab
 * tap swaps the page in place (collection--nav.js), whose fetch() can't reuse
 * a document prefetch (the response varies on Accept) and never activates a
 * prerender. That module warms the target itself on hover / touchstart.
 *
 * Declarative <script type="speculationrules"> in the snippet handles
 * hover/tap intent immediately for Chromium — this JS is a progressive enhancement.
 */

(() => {
  const excluded = /^\/(cart|checkout|account)(\/|$)/;
  const prefetched = new Set();
  const prerendered = new Set();
  const supportsPrerender = HTMLScriptElement.supports?.('speculationrules');

  function getLink(e) {
    const a = e.target.closest?.('a[href]');
    if (!a || a.origin !== location.origin || a.hasAttribute('data-no-prefetch')) return null;
    if (excluded.test(a.pathname)) return null;
    return a;
  }

  function prefetch(href) {
    if (prefetched.has(href)) return;
    prefetched.add(href);
    const l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = href;
    l.as = 'document';
    document.head.appendChild(l);
  }

  function prerender(href) {
    if (!supportsPrerender || prerendered.has(href)) return;
    prerendered.add(href);
    const s = document.createElement('script');
    s.type = 'speculationrules';
    s.textContent = JSON.stringify({ prerender: [{ source: 'list', urls: [href] }] });
    document.head.appendChild(s);
  }

  function init() {
    if (!supportsPrerender) {
      // Desktop: prefetch on hover, prerender after 150ms dwell
      let hoverTimer = 0;
      document.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        const a = getLink(e);
        if (!a) return;
        prefetch(a.href);
        hoverTimer = setTimeout(() => prerender(a.href), 150);
      }, true);

      document.addEventListener('pointerleave', () => {
        clearTimeout(hoverTimer);
      }, true);

      // Mobile: prefetch on touchstart (~100-300ms before click)
      document.addEventListener('touchstart', (e) => {
        const a = getLink(e);
        if (a) prefetch(a.href);
      }, { passive: true, capture: true });
    }


    // PDP → Collection: proactively warm the referring collection so
    // back-navigation is fast. All browsers get a prefetch (HTTP cache hit
    // eliminates TTFB). Chromium desktop also gets a full prerender (~50ms back).
    if (/^\/products\//.test(location.pathname)) {
      const collectionUrl = getReferringCollectionUrl();
      if (collectionUrl) {
        prefetch(collectionUrl);
        if (supportsPrerender && !matchMedia('(pointer: coarse)').matches) {
          prerender(collectionUrl);
        }
      }
    }
  }

  /**
   * Find the collection URL the user navigated from.
   * Priority: Navigation API entries > document.referrer > breadcrumb links.
   * Preserves query params (filters, pagination) for exact page match.
   */
  function getReferringCollectionUrl() {
    const isCollection = (url) => {
      try {
        const u = new URL(url, location.origin);
        return u.origin === location.origin && /^\/collections\//.test(u.pathname);
      } catch { return false; }
    };

    // 1. Navigation API — most reliable, preserves full URL with query params
    if (window.navigation?.currentEntry) {
      const entries = navigation.entries();
      const idx = navigation.currentEntry.index;
      if (idx > 0) {
        const prev = entries[idx - 1].url;
        if (isCollection(prev)) return prev;
      }
    }

    // 2. document.referrer — works cross-browser but may be stripped by policy
    if (document.referrer && isCollection(document.referrer)) {
      return document.referrer;
    }

    // 3. Breadcrumb link — always present on PDP, but loses query params
    const crumbLink = document.querySelector('.breadcrumbs__link[href*="/collections/"]');
    if (crumbLink) return crumbLink.href;

    return null;
  }

  // Defer until browser is idle after load — never competes with LCP
  const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  if (document.readyState === 'complete') {
    schedule(init);
  } else {
    window.addEventListener('load', () => schedule(init), { once: true });
  }
})();
